import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useSelector } from "react-redux";
import { AlertTriangle, Scale, User } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import { FilterDismissLink } from "../../components/dashboard/BackNavLink";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatusBadge from "../../components/dashboard/StatusBadge";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { ESCALATIONS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { MOCK_ESCALATIONS } from "../../data/mock/dashboardData";
import { USE_MOCK_AUTH } from "../../config/api";
import Button from "../../components/ui/Button";
import { fetchOpsEscalations, pushOpsEscalation } from "../../services/institutionWorkspaceApi";
import { showError, showSuccess } from "../../utils/toast";
import { formatPersonName, splitPersonName } from "../../utils/personName";
import { formatCurrency } from "../../data/mock/dashboardData";
import { linkWithReturn } from "../../utils/navigationReturn";
import { useLocation } from "react-router";

const OPEN_ESCALATION_STATUSES = new Set(["with_manager", "with_executive", "with_legal"]);

function normalizeEscalation(raw) {
  if (!raw) return null;
  const owed = Number(raw.amount_owed ?? raw.amountOwed ?? 0);
  const paid = Number(raw.amount_paid ?? raw.amountPaid ?? 0);
  const outstanding = Number(raw.outstanding ?? Math.max(0, owed - paid));
  const row = {
    id: raw.escalation_id || raw.id,
    numericId: raw.id,
    caseId: raw.case_id || raw.caseId,
    customer: raw.customer_name || raw.customer,
    customer_name: raw.customer_name || raw.customer,
    first_name: raw.first_name || "",
    last_name: raw.last_name || "",
    reason: raw.reason,
    legalStatus: raw.status_label || raw.status || raw.legalStatus || "submitted",
    status: raw.status,
    heldBy: raw.assigned_to_name || "—",
    assignedCounsel: raw.submitted_by_name || raw.assignedCounsel || "—",
    escalatedAt: raw.escalated_at
      ? String(raw.escalated_at).slice(0, 10)
      : raw.created_at
        ? String(raw.created_at).slice(0, 10)
        : raw.escalatedAt,
    amountOwed: owed,
    amountPaid: paid,
    outstanding,
    canPush: Boolean(raw.can_push),
    pushTarget: raw.push_target || "",
    legalOfficers: Array.isArray(raw.legal_officers) ? raw.legal_officers : [],
  };
  const names = splitPersonName(row);
  row.first_name = names.first !== "—" ? names.first : row.first_name;
  row.last_name = names.last !== "—" ? names.last : row.last_name;
  return row;
}

function escalationBadgeStatus(row) {
  if (row.status === "closed" || row.legalStatus === "closed") return "closed";
  if (OPEN_ESCALATION_STATUSES.has(row.status)) return "active";
  return "pending";
}

function escalationStatusLabel(row) {
  const short = {
    with_manager: "CCM review",
    with_executive: "Commercial exec.",
    with_legal: "Legal review",
  };
  return short[row.status] || row.legalStatus || "—";
}

function PushEscalation({ row, onDone }) {
  const token = useSelector((state) => state.auth.token);
  const [officerId, setOfficerId] = useState("");
  const [saving, setSaving] = useState(false);

  if (!row.canPush) return <span className="text-xs text-primary/40">—</span>;

  const send = async () => {
    if (row.pushTarget === "legal_officer" && !officerId) {
      showError("Choose a legal officer.");
      return;
    }
    setSaving(true);
    try {
      await pushOpsEscalation(
        token,
        row.numericId,
        row.pushTarget === "legal_officer" ? { legal_officer_id: Number(officerId) } : {}
      );
      showSuccess(
        row.pushTarget === "legal_officer"
          ? "Escalation sent to the legal officer."
          : "Escalation sent to the commercial executive."
      );
      onDone?.();
    } catch (error) {
      showError(error.message || "Could not move this escalation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-w-[12rem] flex-col items-stretch gap-2">
      {row.pushTarget === "legal_officer" ? (
        <select
          className="rounded-lg border border-primary/15 px-2 py-1.5 text-xs text-primary"
          value={officerId}
          onChange={(event) => setOfficerId(event.target.value)}
        >
          <option value="">Legal officer</option>
          {row.legalOfficers.map((officer) => (
            <option key={officer.id} value={officer.id}>
              {officer.name}
            </option>
          ))}
        </select>
      ) : null}
      <Button type="button" size="sm" onClick={send} loading={saving}>
        {row.pushTarget === "legal_officer" ? "Send to legal" : "Send to commercial executive"}
      </Button>
    </div>
  );
}

export default function EscalationsPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const customerParam = searchParams.get("customer");
  const caseIdParam = searchParams.get("caseId");
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [rows, setRows] = useState(() => (useLiveApi ? [] : MOCK_ESCALATIONS));
  const [loading, setLoading] = useState(useLiveApi);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!useLiveApi) {
      setRows(MOCK_ESCALATIONS);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    const query = caseIdParam ? { case_id: caseIdParam } : {};
    fetchOpsEscalations(token, query, { force: reloadKey > 0 })
      .then((data) => {
        if (cancelled) return;
        setRows((data || []).map(normalizeEscalation).filter(Boolean));
      })
      .catch((error) => {
        if (!cancelled) showError(error.message || "Could not load escalations.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi, reloadKey, caseIdParam]);

  const visible = useMemo(() => {
    let list = rows;
    if (customerParam) {
      list = list.filter((row) => row.customer === customerParam);
    }
    return list;
  }, [rows, customerParam]);

  const initialFilters = useMemo(() => {
    if (!customerParam) return undefined;
    return { customer: [customerParam] };
  }, [customerParam]);

  const inReview = visible.filter((e) => OPEN_ESCALATION_STATUSES.has(e.status)).length;

  const columns = [
    { key: "id", header: "ID", type: "id", headerClassName: "w-[11%]" },
    { key: "caseId", header: "Case", type: "id", headerClassName: "w-[14%]" },
    {
      key: "customer_name",
      header: "Customer",
      type: "name",
      headerClassName: "w-[20%]",
      render: (row) => (
        <span className="block truncate" title={formatPersonName(row)}>
          {formatPersonName(row)}
        </span>
      ),
    },
    {
      key: "heldBy",
      header: "With",
      type: "name",
      headerClassName: "w-[18%]",
      render: (row) => (
        <span className="block truncate" title={row.heldBy}>
          {row.heldBy}
        </span>
      ),
    },
    {
      key: "legalStatus",
      header: "Status",
      type: "badge",
      headerClassName: "w-[22%]",
      render: (row) => (
        <span title={row.legalStatus}>
          <StatusBadge
            status={escalationBadgeStatus(row)}
            label={escalationStatusLabel(row)}
          />
        </span>
      ),
    },
    {
      key: "reason",
      header: "Reason",
      type: "text",
      detailOnly: true,
      wide: true,
      render: (row) => <p className="text-sm leading-relaxed text-primary/80">{row.reason || "—"}</p>,
    },
    { key: "assignedCounsel", header: "Submitted by", type: "name", detailOnly: true },
    { key: "escalatedAt", header: "Escalated", type: "date", detailOnly: true },
    {
      key: "amountOwed",
      header: "Owed",
      type: "currency",
      detailOnly: true,
      render: (row) => formatCurrency(row.amountOwed),
    },
    {
      key: "amountPaid",
      header: "Paid",
      type: "currency",
      detailOnly: true,
      render: (row) => formatCurrency(row.amountPaid),
    },
    {
      key: "outstanding",
      header: "Outstanding",
      type: "currency",
      detailOnly: true,
      render: (row) => formatCurrency(row.outstanding),
    },
    {
      key: "push",
      header: "Next step",
      detailOnly: true,
      wide: true,
      render: (row) => <PushEscalation row={row} onDone={() => setReloadKey((value) => value + 1)} />,
    },
  ];

  return (
    <PageShell
      title="Escalations"
      description="Agents send a case to the manager who assigned it. The manager can send it to the commercial executive, who can send it to a legal officer."
      backFallback="/dashboard/cases"
    >
      {caseIdParam && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">Escalations for case</p>
              <p className="text-sm font-bold text-primary">{caseIdParam}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={linkWithReturn(`/dashboard/log-contact?caseId=${encodeURIComponent(caseIdParam)}`, location)}
              className="text-xs font-semibold text-primary/70 underline-offset-2 hover:text-primary hover:underline"
            >
              Open case workspace
            </Link>
            <FilterDismissLink fallback="/dashboard/escalations" fallbackLabel="Show all escalations" />
          </div>
        </div>
      )}

      {customerParam && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              <User className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">Escalations for</p>
              <p className="text-sm font-bold text-primary">{customerParam}</p>
            </div>
          </div>
          <FilterDismissLink fallback="/dashboard/escalations" fallbackLabel="Show all escalations" />
        </div>
      )}

      <StatCardGrid cols={3}>
        <StatCard label="Escalated cases" value={loading ? "…" : visible.length} icon={AlertTriangle} tone="red" />
        <StatCard label="In review" value={loading ? "…" : inReview} icon={Scale} tone="amber" />
        <StatCard
          label="Closed / resolved"
          value={loading ? "…" : visible.filter((row) => ["closed", "Closed"].includes(row.legalStatus)).length}
          icon={Scale}
          tone="primary"
        />
      </StatCardGrid>

      <FilteredDataTable
        key={`escalations-${customerParam ?? "all"}-${caseIdParam ?? "all"}-${loading ? "loading" : "ready"}`}
        data={visible}
        columns={columns}
        config={{ ...ESCALATIONS_FILTER_CONFIG, title: "Escalation filters" }}
        pageSize={10}
        initialFilters={initialFilters}
        emptyMessage={loading ? "Loading escalations…" : "No escalations yet."}
        maxVisibleColumns={5}
        fitContainer
        compact
      />
    </PageShell>
  );
}
